import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, 
  Store, 
  MapPin, 
  Map, 
  Globe, 
  Folder, 
  Tag,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl p-6 border border-surface-border shadow-sm">
    <div className="flex items-center justify-between">
      <div>
        <p className="font-body text-text-muted text-sm mb-1">{title}</p>
        <p className="font-heading text-3xl font-bold text-text-primary">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="text-white" size={24} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  const statCards = [
    { title: 'Cities', value: stats?.cities || 0, icon: MapPin, color: 'bg-blue-500' },
    { title: 'States', value: stats?.states || 0, icon: Map, color: 'bg-green-500' },
    { title: 'Countries', value: stats?.countries || 0, icon: Globe, color: 'bg-purple-500' },
    { title: 'Categories', value: stats?.categories || 0, icon: Folder, color: 'bg-yellow-500' },
    { title: 'Tags', value: stats?.tags || 0, icon: Tag, color: 'bg-pink-500' },
    { title: 'Apartments', value: stats?.apartments || 0, icon: Building2, color: 'bg-brand-primary' },
    { title: 'Vendors', value: stats?.vendors || 0, icon: Store, color: 'bg-teal-500' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-2">Dashboard</h1>
        <p className="font-body text-text-secondary">Welcome to the LocaLS Admin Panel</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <StatCard key={stat.title} {...stat} />
        ))}
      </div>

      <div className="mt-8 bg-white rounded-2xl p-6 border border-surface-border">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="text-brand-primary" size={24} />
          <h2 className="font-heading text-xl font-semibold text-text-primary">Quick Actions</h2>
        </div>
        <p className="font-body text-text-secondary">
          Use the sidebar to navigate to different sections. Start by adding master data like Cities, States, and Categories, 
          then add Apartments and manage Vendors.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
